import React, {ReactNode, useMemo} from 'react';

import {useGate, useStore} from 'effector-react';
import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';

import {fetchGridConfig, saveGridConfig} from 'api/gridsConfig';
import {mapTableColumns, Table} from 'components/ui-kit/Table2.0';
import {
    filtersChanged,
    filtersReset,
    Gate,
    pageNumberChanged,
    sortingsChanged,
    tableState$,
} from 'models/declarations/grid/table';

import {DeclarationsGridFormattedRow, DeclarationsGridRawRow} from 'types/declarations';

interface Props {
    configFunc: (t: TFunction) => Array<any>;
    dataMapper: (
        data: DeclarationsGridRawRow[] | undefined,
        t: TFunction,
    ) => DeclarationsGridFormattedRow[];
    headerControls?: ReactNode;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
}

export const DeclarationsGridTable = ({
    configFunc,
    dataMapper,
    headerControls,
    exportConfig,
}: Props) => {
    const {t} = useTranslation();
    const {data, pageNumber, filters, sortings, dataLoading} = useStore(tableState$);
    useGate(Gate);

    const columns = useMemo(
        () =>
            mapTableColumns(
                configFunc(t),
                filters,
                filtersChanged,
                filtersReset,
                sortings,
                sortingsChanged,
            ),
        [t, filters, sortings],
    );
    const mappedData = React.useMemo(() => dataMapper(data?.data, t), [dataMapper, data, t]);

    return (
        <Table
            columns={columns}
            data={mappedData}
            totalRowsCount={data?.totalElements}
            pageIndex={pageNumber}
            onPageChange={pageNumberChanged}
            hasNext={(data?.page ?? 0) < (data?.totalPages ?? 0)}
            isLoading={dataLoading}
            headerControls={headerControls}
            userConfig={{
                gridName: 'declarations',
                saveConfig: saveGridConfig,
                getConfig: fetchGridConfig,
            }}
            exportConfig={exportConfig}
        />
    );
};
